import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "sm:flex gap-2 sm:gap-4 mt-4 sm:mt-6" }
const _hoisted_2 = { class: "flex flex-col" }
const _hoisted_3 = { class: "h-fit w-full sm:mt-0 mt-6" }
const _hoisted_4 = { class: "flex items-center gap-2 ml-auto rounded-md bg-gray-700 px-4 py-2 max-w-fit mb-4" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("label", {
  for: "redirect-label",
  class: "text-white text-ellipsis whitespace-nowrap overflow-hidden"
}, "Redirect Mode", -1)
const _hoisted_6 = { class: "flex gap-2 sm:gap-4 max-w-fit ml-auto" }

import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { Input, TreeSelect, Button } from 'ant-design-vue';
import { CopyOutlined, UploadOutlined } from '@ant-design/icons-vue';
import SpinnerLoading from '@/components/shared/SpinnerLoading.vue';
import ImageLoading from '@/components/shared/ImageLoading.vue';
import { Hitomi } from '@/model/hitomi';
import { getHitomiImage } from '@/repository/hitomi';
import { scraping } from '@/repository/centralized';
import { capitalize } from '@/util/universal';
import LocalStorageKey from '@/model/key';


export default /*#__PURE__*/_defineComponent({
  __name: 'HitomiInformation',
  props: {
    hitomi: {},
    isLoading: { type: Boolean }
  },
  emits: ["submit", "reset"],
  setup(__props: any, { emit }) {

const props = __props;




const linkStatus = ref('');
const _isLoading = ref(props.isLoading);
const isRedirect = ref(localStorage.getItem(LocalStorageKey.HitomiRedirect) === 'on');
const imageBlobURL = ref('');
const reload = ref(false);
const fileInput = ref<HTMLInputElement>();
const hitomiData = ref<Hitomi>(props.hitomi);
const store = useStore();

watch(isRedirect, () =>
	localStorage.setItem(LocalStorageKey.HitomiRedirect, isRedirect.value ? 'on' : 'off')
);
watch(
	() => props.isLoading,
	() => (_isLoading.value = props.isLoading)
);
watch(
	() => props.hitomi,
	() => {
		hitomiData.value = props.hitomi;
		linkStatus.value = 'success';
		resetFileInput();
	}
);

const submit = async () => {
	if (!linkStatus.value.length) {
		await scrapingHitomiContent();
	}
	if (linkStatus.value !== 'success') {
		store.dispatch('alert', { type: 'warning', message: 'please check any progress!' });
		return;
	}
	if (!_isLoading.value && !reload.value) {
		await emit('submit', isRedirect.value, props.hitomi, fileInput?.value?.files?.item(0) as File);
	} else {
		store.dispatch('alert', { type: 'warning', message: 'please wait for any progress!' });
	}
};
const reset = () => {
	resetFileInput();
	emit('reset');
};

async function resetFileInput() {
	if (fileInput.value) {
		reload.value = true;
		if (!props.hitomi.hitomiUUID) {
			fileInput.value.value = '';
			imageBlobURL.value = '';
		} else {
			const res = await getHitomiImage(props.hitomi.hitomiImageURL);
			if (res) {
				const url = URL.createObjectURL(res);
				fileInput.value.src = url;
				imageBlobURL.value = url;
			}
		}
		reload.value = false;
	}
}

function mapSelect(texts: string[]) {
	return texts.map((text) => {
		return {
			value: text,
			title: text,
			label: text,
			key: text
		};
	});
}

async function scrapingHitomiContent(callback?: (data: { image: string; body: string }) => void) {
	if (
		!hitomiData?.value?.hitomiLink.includes('https://hitomi.la/gamecg/') ||
		!hitomiData?.value?.hitomiLink.length
	) {
		linkStatus.value = 'warning';
		return;
	}

	if (
		!callback &&
		hitomiData.value.hitomiLink.includes('#') &&
		props.hitomi.hitomiLink.length &&
		props.hitomi.hitomiLink.includes('#')
	) {
		const baseHitomiLink = props.hitomi.hitomiLink.substring(
			0,
			props.hitomi.hitomiLink.lastIndexOf('#')
		);
		const currentHitomiLink = hitomiData?.value?.hitomiLink.substring(
			0,
			hitomiData?.value?.hitomiLink.lastIndexOf('#')
		);
		if (baseHitomiLink === currentHitomiLink) {
			linkStatus.value = 'success';
			return;
		}
	}

	_isLoading.value = true;
	try {
		const res = await scraping<{ image: string; body: string }>({
			url: hitomiData.value.hitomiLink,
			querys: [
				{
					name: 'image',
					selector:
						'body > div.container > div.content > div.cover-column.lillie > div.cover > a > picture > img',
					target: 'attribute',
					attribute: 'src'
				},
				{
					name: 'body',
					selector: 'div.container > div.content',
					target: 'innerHTML'
				}
			]
		});
		if (!res) {
			throw Error('something went wrong, please try again!');
		}

		if (callback) {
			callback(res);
		}

		linkStatus.value = 'success';
	} catch (error) {
		linkStatus.value = 'error';
		store.dispatch('alert', { type: 'error', message: 'something went wrong, please try again!' });
	} finally {
		_isLoading.value = false;
	}
}

async function extractHitomi() {
	await scrapingHitomiContent((res) => {
		const div = document.createElement('div');
		div.innerHTML = res.body;
		const extractContent = (selector: string) => {
			const texts: string[] = [];
			const element = div.querySelector(selector);
			if (element) {
				element.querySelectorAll('li')?.forEach((elm) => {
					const name = elm.querySelector('a');
					if (name) {
						texts.push(capitalize(name.innerHTML, ' '));
					}
				});
			}
			return texts;
		};

		hitomiData.value.hitomiTitle = div.querySelector('#gallery-brand > a')?.innerHTML || '';
		hitomiData.value.hitomiArtists = extractContent('#artists');
		hitomiData.value.hitomiGroups = extractContent('#groups');
		hitomiData.value.hitomiTags = extractContent('#tags');
		hitomiData.value.hitomiImageURL = res.image;
		setHitomiImage(res.image);
	});
}

async function setHitomiImage(imageURL: string) {
	reload.value = true;
	const res = await getHitomiImage(imageURL);
	if (res) {
		imageBlobURL.value = URL.createObjectURL(res);
		if (fileInput.value) {
			const container = new DataTransfer();
			const file = new File([res], imageURL);
			container.items.add(file);
			fileInput.value.files = container.files;
		}
	}
	reload.value = false;
}

function changeImage(e: Event) {
	const fileInput = e.target as HTMLInputElement;
	if (fileInput?.files?.length === 1) {
		reload.value = true;
		imageBlobURL.value = URL.createObjectURL(fileInput.files[0]);
		reload.value = false;
	}
}

return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_switch = _resolveComponent("a-switch")!
  const _component_a_form = _resolveComponent("a-form")!

  return (_openBlock(), _createBlock(SpinnerLoading, { loading: _isLoading.value }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(ImageLoading, {
            src: imageBlobURL.value,
            reload: reload.value,
            previewable: true,
            "img-class": "!h-72 !w-72",
            "loading-class": "h-72 w-72 m-auto sm:m-0"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_a_button, {
                onClick: _cache[0] || (_cache[0] = () => fileInput.value?.click()),
                class: "sm:hidden my-4 rounded-full absolute left-1/2 translate-x-32 translate-y-[15.5rem]"
              }, {
                icon: _withCtx(() => [
                  _createVNode(_unref(UploadOutlined))
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["src", "reload"]),
          _createElementVNode("input", {
            type: "file",
            ref_key: "fileInput",
            ref: fileInput,
            accept: "image/*",
            hidden: "",
            onChange: changeImage
          }, null, 544)
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_a_form, {
            layout: "horizontal",
            model: _ctx.hitomi,
            onFinish: _cache[7] || (_cache[7] = ($event: any) => (submit()))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_a_form_item, {
                label: "Link",
                name: "hitomiLink",
                "has-feedback": "",
                "validate-status": linkStatus.value,
                rules: [{ required: true, message: 'Please input hitomi link' }]
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Input).Group, { compact: "" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Input), {
                        value: hitomiData.value.hitomiLink,
                        "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((hitomiData.value.hitomiLink) = $event)),
                        class: "!w-[calc(100%-2rem)] !text-start",
                        placeholder: "Enter hitomi link that start with `https://hitomi.la/gamecg/`",
                        onChange: _cache[2] || (_cache[2] = () => (linkStatus.value = ''))
                      }, null, 8, ["value"]),
                      _createVNode(_component_a_button, {
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (extractHitomi()))
                      }, {
                        icon: _withCtx(() => [
                          _createVNode(_unref(CopyOutlined))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["validate-status"]),
              _createVNode(_component_a_form_item, {
                label: "Title",
                name: "hitomiTitle",
                "has-feedback": "",
                rules: [{ required: true, message: 'Please input hitomi title' }]
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Input), {
                    value: hitomiData.value.hitomiTitle,
                    "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((hitomiData.value.hitomiTitle) = $event)),
                    placeholder: "Enter hitomi title"
                  }, null, 8, ["value"])
                ]),
                _: 1
              }),
              _createVNode(_component_a_form_item, {
                label: "Artists",
                name: "hitomiArtists"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(TreeSelect), {
                    value: hitomiData.value.hitomiArtists,
                    style: {"width":"100%"},
                    "tree-data": mapSelect(hitomiData.value.hitomiArtists || []),
                    "tree-checkable": "",
                    class: "text-start",
                    placeholder: "Please select hitomi artists",
                    "tree-node-filter-prop": "label"
                  }, null, 8, ["value", "tree-data"])
                ]),
                _: 1
              }),
              _createVNode(_component_a_form_item, {
                label: "Groups",
                name: "hitomiGroups"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(TreeSelect), {
                    value: hitomiData.value.hitomiGroups,
                    style: {"width":"100%"},
                    "tree-data": mapSelect(hitomiData.value.hitomiGroups || []),
                    "tree-checkable": "",
                    class: "text-start",
                    placeholder: "Please select hitomi groups",
                    "tree-node-filter-prop": "label"
                  }, null, 8, ["value", "tree-data"])
                ]),
                _: 1
              }),
              _createVNode(_component_a_form_item, {
                label: "Tags",
                name: "hitomiTags"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(TreeSelect), {
                    value: hitomiData.value.hitomiTags,
                    style: {"width":"100%"},
                    "tree-data": mapSelect(hitomiData.value.hitomiTags || []),
                    "tree-checkable": "",
                    class: "text-start",
                    placeholder: "Please select hitomi tags",
                    "tree-node-filter-prop": "label"
                  }, null, 8, ["value", "tree-data"])
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_4, [
                _hoisted_5,
                _createVNode(_component_a_switch, {
                  checked: isRedirect.value,
                  "onUpdate:checked": _cache[5] || (_cache[5] = ($event: any) => ((isRedirect).value = $event))
                }, null, 8, ["checked"])
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_unref(Button), {
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (reset()))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Reset")
                  ]),
                  _: 1
                }),
                _createVNode(_unref(Button), {
                  "html-type": "submit",
                  type: "primary"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Submit")
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }, 8, ["model"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["loading"]))
}
}

})